'use client';

import { TrendingUp, TrendingDown } from 'lucide-react';
import type { TokenIntel } from '@/lib/web3/types';

interface Props {
  intel: TokenIntel;
}

function fmtUsd(n: number | null): string {
  if (n == null || n <= 0) return '-';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n.toFixed(0)}`;
}

function PriceChange({ val }: { val: number | null }) {
  if (val == null) return <span className="text-neutral-600">—</span>;
  const pos = val >= 0;
  const abs = Math.abs(val);
  return (
    <span className={`flex items-center gap-1 font-mono font-semibold text-lg ${pos ? 'text-emerald-400' : 'text-rose-400'}`}>
      {pos ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
      {pos ? '+' : '-'}{abs.toFixed(2)}%
    </span>
  );
}

function BuyBar({ buy, sell }: { buy: number | null; sell: number | null }) {
  const b = buy ?? 0;
  const s = sell ?? 0;
  const total = b + s;
  if (total === 0) return null;
  const pct = Math.round((b / total) * 100);
  return (
    <div className="mt-2">
      <div className="h-1 w-full bg-white/5 overflow-hidden flex">
        <div className="h-full bg-emerald-500/60" style={{ width: `${pct}%` }} />
        <div className="h-full bg-rose-500/50 flex-1" />
      </div>
      <div className="flex justify-between mt-0.5 text-[9px] text-neutral-600">
        <span>{pct}% buy</span>
        <span>{100 - pct}% sell</span>
      </div>
    </div>
  );
}

interface Frame {
  label: string;
  price: number | null;
  buy: number | null;
  sell: number | null;
}

export default function TimeframeGrid({ intel }: Props) {
  const frames: Frame[] = [
    { label: '1H',  price: intel.priceChange1h,  buy: intel.buyVolume1h,  sell: intel.sellVolume1h },
    { label: '6H',  price: intel.priceChange6h,  buy: intel.buyVolume6h,  sell: intel.sellVolume6h },
    { label: '24H', price: intel.priceChange24h, buy: intel.buyVolume24h, sell: intel.sellVolume24h },
  ];

  const hasAny = frames.some((f) => f.price != null || f.buy != null);
  if (!hasAny) return null;

  return (
    <div className="border border-white/10 bg-black/40 p-5">
      <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-4">
        Price &amp; volume momentum
      </p>
      <div className="grid grid-cols-3 gap-3">
        {frames.map((f) => (
          <div key={f.label} className="border border-white/8 bg-white/[0.02] p-3">
            <p className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">{f.label}</p>
            <PriceChange val={f.price} />
            <div className="mt-2 space-y-0.5">
              {f.buy != null && (
                <p className="text-[10px] text-emerald-500/80 font-mono">↑ {fmtUsd(f.buy)}</p>
              )}
              {f.sell != null && (
                <p className="text-[10px] text-rose-500/80 font-mono">↓ {fmtUsd(f.sell)}</p>
              )}
            </div>
            <BuyBar buy={f.buy} sell={f.sell} />
          </div>
        ))}
      </div>
    </div>
  );
}
